import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { describeError } from '../describeError';
import { formatDate, formatDateRange, formatMoney, sourceSystemLabel } from '../format';
import { useAsyncData } from '../hooks/useAsyncData';
import { payrollHistoryImportGateway as gateway } from '../index';
import { batchStepPath } from '../routes';
import type { BatchDetail, Preview, PreviewRun } from '../types';
import { ErrorSummary } from './basics';
import { StatementList } from './StatementList';

function RunDetail({ batchId, runId }: { batchId: string; runId: string }) {
  const load = useCallback((signal: AbortSignal) => gateway.getPreviewRun(batchId, runId, signal), [batchId, runId]);
  const { loading, data, error, reload } = useAsyncData(load);
  if (loading) return <p className="muted" role="status">Loading pay statements…</p>;
  if (error !== undefined || !data) {
    return (
      <div>
        <ErrorSummary message={describeError(error, 'Could not load the pay statements for this run.')} />
        <button type="button" className="phi-btn-secondary" onClick={reload}>Try again</button>
      </div>
    );
  }
  return <StatementList statements={data.statements} />;
}

function RunsTable({ batchId, runs }: { batchId: string; runs: PreviewRun[] }) {
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const openRun = runs.find((r) => r.sourcePayrollRunId === openRunId) ?? null;
  const panelId = 'phi-run-detail-panel';
  return (
    <div>
      <div className="table-scroll">
        <table className="phi-table-wide">
          <caption>Payroll runs in this import</caption>
          <thead>
            <tr>
              <th scope="col">Pay date</th>
              <th scope="col">Pay period</th>
              <th scope="col">Type</th>
              <th scope="col">Status</th>
              <th scope="col" className="num">Employees</th>
              <th scope="col" className="num">Gross wages</th>
              <th scope="col" className="num">Employee taxes</th>
              <th scope="col" className="num">Deductions</th>
              <th scope="col" className="num">Employer taxes</th>
              <th scope="col" className="num">Net pay</th>
              <th scope="col" className="num">Voided / corrected</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => {
              const open = openRunId === run.sourcePayrollRunId;
              return (
                <tr key={run.sourcePayrollRunId}>
                  <th scope="row">
                    {formatDate(run.payDate)}
                    {/* In the first column so opening a run never scrolls the table sideways. */}
                    <div className="phi-row-action">
                      <button
                        type="button"
                        className="phi-btn-secondary"
                        aria-expanded={open}
                        aria-controls={panelId}
                        aria-label={`${open ? 'Hide' : 'View'} statements for ${formatDate(run.payDate)}`}
                        onClick={() => setOpenRunId(open ? null : run.sourcePayrollRunId)}
                      >
                        {open ? 'Hide' : 'View'} statements
                      </button>
                    </div>
                  </th>
                  <td>{formatDateRange(run.periodStart, run.periodEnd)}</td>
                  <td>{run.payrollType}</td>
                  <td>{run.runStatus}{run.alreadyImported ? ' (already imported)' : ''}</td>
                  <td className="num">{run.employeeCount}</td>
                  <td className="num">{formatMoney(run.grossPay)}</td>
                  <td className="num">{formatMoney(run.employeeTaxes)}</td>
                  <td className="num">{formatMoney(run.employeeDeductions)}</td>
                  <td className="num">{formatMoney(run.employerTaxes)}</td>
                  <td className="num">{formatMoney(run.netPay)}</td>
                  <td className="num">{run.voidedStatementCount} / {run.correctedStatementCount}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* The detail opens below the table (not inside a row) so wide line-item tables never stretch the runs table. */}
      <div id={panelId}>
        {openRun && (
          <section aria-labelledby="phi-run-detail-heading">
            <h3 id="phi-run-detail-heading">Pay statements for pay date {formatDate(openRun.payDate)}</h3>
            <RunDetail batchId={batchId} runId={openRun.sourcePayrollRunId} />
          </section>
        )}
      </div>
    </div>
  );
}

function periodRange(p: Preview): string {
  if (p.runs.length === 0) return '—';
  const starts = p.runs.map((r) => r.periodStart).sort();
  const ends = p.runs.map((r) => r.periodEnd).sort();
  return formatDateRange(starts[0] ?? null, ends[ends.length - 1] ?? null);
}

function PreviewSummary({ p }: { p: Preview }) {
  return (
    <dl className="phi-summary" aria-label="Import preview totals">
      <div><dt>Source platform</dt><dd className="phi-summary-text">{sourceSystemLabel(p.sourcePlatform)}</dd></div>
      <div><dt>File name</dt><dd className="phi-summary-text">{p.fileName ?? '—'}</dd></div>
      <div><dt>Payroll company</dt><dd className="phi-summary-text">{p.payrollCompany ?? '—'}</dd></div>
      <div><dt>Pay period range</dt><dd className="phi-summary-text">{periodRange(p)}</dd></div>
      <div><dt>Pay date range</dt><dd className="phi-summary-text">{formatDateRange(p.payDateStart, p.payDateEnd)}</dd></div>
      <div><dt>Payroll runs</dt><dd>{p.runCount}</dd></div>
      <div><dt>Employees</dt><dd>{p.employeeCount}</dd></div>
      <div><dt>Gross wages</dt><dd>{formatMoney(p.grossPay)}</dd></div>
      <div><dt>Employee taxes</dt><dd>{formatMoney(p.employeeTaxes)}</dd></div>
      <div><dt>Employer taxes</dt><dd>{formatMoney(p.employerTaxes)}</dd></div>
      <div><dt>Deductions</dt><dd>{formatMoney(p.employeeDeductions)}</dd></div>
      <div><dt>Net pay</dt><dd>{formatMoney(p.netPay)}</dd></div>
      <div><dt>Voided runs / statements</dt><dd>{p.voidedRunCount} / {p.voidedStatementCount}</dd></div>
      <div><dt>Corrected statements</dt><dd>{p.correctedStatementCount}</dd></div>
      <div><dt>Warnings</dt><dd>{p.warningCount}</dd></div>
      <div><dt>Errors</dt><dd>{p.errorCount}</dd></div>
    </dl>
  );
}

export function PreviewStep({ batch }: { batch: BatchDetail }) {
  const load = useCallback((signal: AbortSignal) => gateway.getPreview(batch.id, signal), [batch.id]);
  const { loading, data, error, reload } = useAsyncData(load);

  if (loading) return <p className="muted" role="status">Loading preview…</p>;
  if (error !== undefined || !data) {
    return (
      <div>
        <ErrorSummary message={describeError(error, 'Could not load the preview.')} />
        <button type="button" className="phi-btn-secondary" onClick={reload}>Try again</button>
      </div>
    );
  }

  return (
    <div>
      <p>
        This is a read-only preview of what will be kept as historical payroll records. Nothing has been imported yet.
      </p>
      <PreviewSummary p={data} />
      <p className="muted">Net pay includes any reimbursements shown on individual pay statements. Voided statements are shown for reference and excluded from totals.</p>
      <RunsTable batchId={batch.id} runs={data.runs} />
      <div className="phi-footer">
        <Link to={batchStepPath(batch.id, 'employees')} className="phi-btn-secondary">Back</Link>
        <Link to={batchStepPath(batch.id, 'problems')} className="phi-btn-primary">Next: Fix problems</Link>
      </div>
    </div>
  );
}
